import React, { createContext, useContext, useState, ReactNode } from 'react'
import { Snackbar, Typography } from '@mui/material'
import { ToastAlert } from './toast-provider.styles'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export type ToastConfig = {
	message: string,
	title?: string,
	type?: ToastType,
	duration?: number,
	action?: ReactNode,
}

type ToastContextType = {
	showToast: ( config: ToastConfig ) => void,
	hideToast: () => void,
}

const ToastContext = createContext<ToastContextType | undefined>( undefined )

export const ToastProvider: React.FC<{ children: ReactNode }> = ( { children } ) => {
	const [ toastConfig, setToastConfig ] = useState<ToastConfig | null>( null )

	const showToast = ( config: ToastConfig ) => {
		setToastConfig( config )
	}

	const hideToast = () => {
		setToastConfig( null )
	}

	const autoHideDuration = toastConfig?.type !== 'error' ? toastConfig?.duration : undefined

	return (
		<ToastContext.Provider value={{ showToast, hideToast }}>
			{children}
			<Snackbar
				open={!!toastConfig}
				autoHideDuration={autoHideDuration}
				onClose={hideToast}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<ToastAlert
					onClose={hideToast}
					severity={toastConfig?.type ?? 'info'}
					action={toastConfig?.action}
				>
					<Typography variant="subtitle1" fontWeight="bold">
						{toastConfig?.title}
					</Typography>
					<Typography variant="body2">
						{toastConfig?.message}
					</Typography>
				</ToastAlert>
			</Snackbar>
		</ToastContext.Provider>
	)
}

export const useToast = () => {
	const context = useContext( ToastContext )
	if ( !context ) {
		throw new Error( 'useToast must be used within a ToastProvider' )
	}
	return context
}
